import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Box,
  Cards,
  Container,
  Grid,
  Header,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  TextFilter,
  Toggle,
} from '@amzn/awsui-components-react-v3';
import { SelectProps } from '@amzn/awsui-components-react-v3/polaris/select/interfaces';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { usePrimitiveMetricsData } from './hooks/usePrimitiveMetricsData';
import { SdMetric } from './types/metrics.types';
import {
  ALL_OPTION,
  BLOCK_CARDS_PER_ROW,
  CHART_BAR_ROW_HEIGHT,
  CHART_MIN_HEIGHT,
  COVERAGE_PERCENT_DOMAIN,
  FILTER_GRID_DEFINITION,
  PAGE_SIZE,
  SD_METRIC_COLUMN_DEFINITIONS,
  getStatus,
  valueText,
} from './constants/primitive-metrics.constants';

// Re-exported so existing consumers/tests can import the value helpers from this module.
export { valueText, getStatus };

const matchesCategory = (m: SdMetric, category: string) =>
  category === 'all' || m.metricClassification === category;

// Empty / no-match placeholder for the chart and table.
const emptyState = (title: string, subtitle?: string) => (
  <Box textAlign="center" color="inherit">
    <Box fontWeight="bold">{title}</Box>
    {subtitle && (
      <Box variant="p" color="inherit">
        {subtitle}
      </Box>
    )}
  </Box>
);

export const PrimitiveMetrics = React.memo((): JSX.Element => {
  const { metrics, stage, isLoading } = usePrimitiveMetricsData();

  const [showChart, setShowChart] = useState(false);
  const [tableCategory, setTableCategory] = useState<SelectProps.Option>(ALL_OPTION);

  const categoryOptions = useMemo<SelectProps.Option[]>(() => {
    const cats = Array.from(new Set(metrics.map((m) => m.metricClassification).filter(Boolean)));
    return [ALL_OPTION, ...cats.map((c) => ({ label: c, value: c }))];
  }, [metrics]);

  // Only percentages fit a 0-100 bar chart.
  const chartMetricsdata = useMemo(
    () =>
      metrics
        .filter((m) => m.valueType === 'PERCENTAGE' && m.value !== null)
        .map((m) => ({ x: m.metricName, y: m.value as number })),
    [metrics]
  );

  // Everything the chart can't show: numbers, strings, dates, and empty percentages.
  const blockMetricsdata = useMemo(
    () => metrics.filter((m) => m.valueType !== 'PERCENTAGE' || m.value === null),
    [metrics]
  );

  // Category dropdown filters first; useCollection handles search + paging.
  const filteredMetrics = useMemo(
    () => metrics.filter((m) => matchesCategory(m, tableCategory.value ?? 'all')),
    [metrics, tableCategory]
  );

  const { items, filteredItemsCount, filterProps, paginationProps, actions } = useCollection(filteredMetrics, {
    filtering: {
      filteringFunction: (m, text) => {
        const t = text.trim().toLowerCase();
        return (
          !t ||
          m.metricName.toLowerCase().includes(t) ||
          m.description.toLowerCase().includes(t) ||
          m.metricClassification.toLowerCase().includes(t)
        );
      },
    },
    pagination: { pageSize: PAGE_SIZE },
  });

  const matchCount = filteredItemsCount ?? items.length;

  return (
    <Container
      header={
        <Header variant="h2" description={`Point in time metrics for stage ${stage}.`}>
          Primitive Point in Time Metrics for SDO
        </Header>
      }
      disableContentPaddings
    >
      <Box padding="l">
        <SpaceBetween size="l">
          <Toggle
            checked={showChart}
            onChange={({ detail }) => setShowChart(detail.checked)}
            data-testid="coverage-chart-toggle"
          >
            View bar chart
          </Toggle>

          {showChart && (
            <SpaceBetween size="l">
              <Container
                header={
                  <Header variant="h3" description={`Data point coverage percentage for stage ${stage}.`}>
                    Coverage percentage
                  </Header>
                }
              >
                <BarChart
                  height={Math.max(CHART_MIN_HEIGHT, chartMetricsdata.length * CHART_BAR_ROW_HEIGHT)}
                  horizontalBars
                  hideFilter
                  xScaleType="categorical"
                  xTitle="Coverage (%)"
                  yTitle="Metric name"
                  yDomain={COVERAGE_PERCENT_DOMAIN}
                  series={[{ title: 'Data point coverage', type: 'bar', data: chartMetricsdata }]}
                  empty={emptyState('No data available')}
                  noMatch={emptyState('No matching data')}
                />
              </Container>

              {blockMetricsdata.length > 0 && (
                <Container
                  header={<Header variant="h3">Counts &amp; status metrics</Header>}
                >
                  <Cards
                    data-testid="sd-metric-blocks"
                    items={blockMetricsdata}
                    trackBy="metricName"
                    cardsPerRow={BLOCK_CARDS_PER_ROW}
                    cardDefinition={{
                      header: (m: SdMetric) => m.metricName,
                      sections: [
                        {
                          id: 'value',
                          content: (m: SdMetric) => (
                            <Box fontWeight="bold" color="text-status-info">
                              {valueText(m)}
                            </Box>
                          ),
                        },
                      ],
                    }}
                  />
                </Container>
              )}
            </SpaceBetween>
          )}

          <Table
            data-testid="sd-metrics-summary-table"
            columnDefinitions={SD_METRIC_COLUMN_DEFINITIONS}
            items={items}
            trackBy="metricName"
            loading={isLoading}
            loadingText="Loading metrics"
            resizableColumns
            stickyHeader
            stripedRows
            header={<Header counter={`(${matchCount})`}>All metrics</Header>}
            filter={
              <Grid gridDefinition={FILTER_GRID_DEFINITION}>
                <TextFilter
                  {...filterProps}
                  filteringPlaceholder="Find metric"
                  countText={`${matchCount} ${matchCount === 1 ? 'match' : 'matches'}`}
                />
                <Select
                  inlineLabelText="Classification"
                  options={categoryOptions}
                  selectedOption={tableCategory}
                  onChange={({ detail }) => {
                    setTableCategory(detail.selectedOption);
                    actions.setCurrentPage(1);
                  }}
                  data-testid="sd-table-category-filter"
                />
              </Grid>
            }
            pagination={<Pagination {...paginationProps} />}
            empty={emptyState('No metrics found', 'No metrics match your search.')}
          />
        </SpaceBetween>
      </Box>
    </Container>
  );
});

PrimitiveMetrics.displayName = 'PrimitiveMetrics';
